import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { grantFindingApplicationStrategies } from '../content/educational/grantFindingApplicationStrategies';

const processKeywords = (text, keywords) => {
  if (!keywords || !Array.isArray(keywords)) return text;
  
  let processedText = text;
  keywords.forEach(keyword => {
    if (keyword && typeof keyword === 'string') {
      const regex = new RegExp(`(${keyword})`, 'gi');
      processedText = processedText.replace(regex, '**$1**');
    }
  });
  return processedText;
};

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;
    const lineSpacing = 1.5;
    const paragraphSpacing = 0.5;
    const bulletIndent = 8;
    const numberIndent = 5;
    const titleSpacing = 10;
    const titleLineSpacing = 6;
    const sectionSpacing = 3.5;
    const contentSpacing = 2.5;
    const pageBreakThreshold = 8;

    // Update font sizes
    const fonts = {
      title: 22,
      sectionHeading: 14,
      subHeading: 12,
      body: 11,
      footer: 8
    };

    // Add color constants
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255]
    };

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 35;
      const qrSize = 22;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 4, 4, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to start?', pageWidth / 2, yPosition + 13, { align: 'center' });

      doc.setFontSize(10);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 22, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 6, yPosition + 6.5, qrSize, qrSize);
    };

    // Process markdown text
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Update renderTextWithBold function
    const renderTextWithBold = (text, x, y, isAfterHeader = false) => {
      const segments = processMarkdown(text);
      let currentX = x;
      
      // Check if this is an Example Alert with multiple quoted parts
      const isExampleAlert = text.includes('Example Alert:');
      
      segments.forEach(segment => {
        doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
        
        if (isExampleAlert) {
          if (segment.text.startsWith('Example Alert:')) {
            // Render "Example Alert:" in gray
            doc.setTextColor(...colors.gray);
            doc.text('Example Alert: ', currentX, y);
            currentX += doc.getTextWidth('Example Alert: ');
          } else {
            // Split by quotes and preserve quotes in the result
            const parts = segment.text.split(/(".*?")/g).filter(Boolean);
            parts.forEach(part => {
              if (part.startsWith('"') && part.endsWith('"')) {
                // Quoted content in green
                doc.setTextColor(...colors.green);
              } else {
                // Non-quoted content (like "AND") in gray
                doc.setTextColor(...colors.gray);
              }
              doc.text(part, currentX, y);
              currentX += doc.getTextWidth(part);
            });
          }
        } else if (segment.text.startsWith('Example')) {
          // Handle other examples as before
          const parts = segment.text.split(': ');
          if (parts.length > 1) {
            doc.setTextColor(...colors.gray);
            doc.text(parts[0] + ': ', currentX, y);
            currentX += doc.getTextWidth(parts[0] + ': ');
            
            doc.setTextColor(...colors.green);
            doc.text(parts[1], currentX, y);
            currentX += doc.getTextWidth(parts[1]);
          }
        } else {
          // Regular text handling
          doc.setTextColor(...colors.gray);
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        }
      });
      
      // Reset after rendering
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.gray);
    };

    // Process lists with consistent formatting
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 12;
      const numberIndent = 8;
      const nestedIndent = 8;
      let previousLineWasHeader = false;
      
      lines.forEach(line => {
        const isNested = line.startsWith('   ') || line.startsWith('\t');
        const baseIndent = isNested ? bulletIndent + nestedIndent : bulletIndent;
        const trimmedLine = line.trim();

        // Handle section headers
        if (trimmedLine.startsWith('###')) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(fonts.subHeading);
          doc.setTextColor(5, 150, 105);
          doc.text(trimmedLine.replace('###', '').trim(), margin, currentY);
          currentY += 6;
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(31, 41, 55);
          previousLineWasHeader = true;
          return;
        } else {
          previousLineWasHeader = false;
        }

        // Handle numbered lists with bold titles
        const numberedMatch = trimmedLine.match(/^(\d+\))\s*\*\*(.*?)\*\*:\s*(.*)$/);
        if (numberedMatch) {
          const [, number, title, content] = numberedMatch;
          const x = margin + (isNested ? numberIndent + nestedIndent : numberIndent);
          
          // Render number
          doc.setFont('helvetica', 'normal');
          doc.text(number, x - 4, currentY);
          
          // Render bold title
          doc.setFont('helvetica', 'bold');
          doc.text(title + ':', x + 4, currentY);
          const titleWidth = doc.getTextWidth(title + ': ');
          
          // Render content
          doc.setFont('helvetica', 'normal');
          const contentLines = doc.splitTextToSize(content, contentWidth - (x + titleWidth + 8));
          contentLines.forEach((line, index) => {
            if (index === 0) {
              doc.text(line, x + titleWidth + 4, currentY);
            } else {
              currentY += 3.5;
              doc.text(line, x + 4, currentY);
            }
          });
          currentY += 5;
          return;
        }

        // Handle bullet points
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
          doc.setTextColor(...colors.gray);
          const bulletText = trimmedLine.substring(1).trim();
          const bulletSymbol = '•';
          
          // Calculate indentation consistently
          const indentLevel = line.search(/\S|$/) / 2;
          const baseIndent = margin + bulletIndent;
          const bulletX = baseIndent + (indentLevel * nestedIndent);
          const textX = bulletX + 8;
          
          // Set consistent font settings
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(fonts.body);
          
          const processedBullet = processKeywords(bulletText, keywords);
          const maxWidth = contentWidth - (bulletX - margin + 20);
          const bulletLines = doc.splitTextToSize(processedBullet, maxWidth);
          
          // Store initial settings
          const settings = {
            font: 'helvetica',
            size: fonts.body,
            color: colors.gray,
            indent: {
              bullet: bulletX,
              text: textX
            }
          };
          
          // Calculate total height needed
          const totalHeight = (bulletLines.length * 5) + 3;
          
          // Start new page if bullet won't fit
          if (currentY + totalHeight > pageHeight - footerHeight - 15) {
            doc.addPage();
            addHeader();
            addFooter(doc.internal.getNumberOfPages());
            currentY = headerHeight + 12;
          }
          
          // Apply consistent settings
          const applySettings = () => {
            doc.setFont(settings.font, 'normal');
            doc.setFontSize(settings.size);
            doc.setTextColor(...settings.color);
          };
          
          // Render first line
          applySettings();
          doc.text(bulletSymbol, settings.indent.bullet, currentY);
          renderTextWithBold(bulletLines[0], settings.indent.text, currentY);
          currentY += 5;
          
          // Handle remaining lines
          for (let i = 1; i < bulletLines.length; i++) {
            if (currentY + 5 > pageHeight - footerHeight - 15) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
              applySettings();
            }
            
            renderTextWithBold(bulletLines[i], settings.indent.text, currentY);
            currentY += 5;
          }
          
          currentY += 3;
        } else {
          // Regular text handling
          const lines = doc.splitTextToSize(trimmedLine, contentWidth);
          lines.forEach((line, index) => {
            // Check for page break
            if (currentY + 4 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
              
              // Reset font settings after page break
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(fonts.body);
              doc.setTextColor(31, 41, 55);
            }
            
            // Reset font size before each line
            doc.setFontSize(fonts.body);
            if (index === 0 && previousLineWasHeader) {
              doc.setTextColor(37, 99, 235);
              doc.text(line, margin, currentY);
              doc.setTextColor(31, 41, 55);
            } else {
              doc.setTextColor(31, 41, 55);
              doc.text(line, margin, currentY);
            }
            currentY += 3.5;
          });
          currentY += 2;
          previousLineWasHeader = false;
        }
      });

      return currentY;
    };

    // Content generation
    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with increased spacing
      doc.setFontSize(fonts.title);
      doc.setTextColor(37, 99, 235);  // Set blue color
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      yPosition += (titleLines.length * 10) + 15; // Add more space after title
      
      content.forEach((section, index) => {
        // Check if section will fit on current page
        if (yPosition + 50 > pageHeight - footerHeight - 15) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.setTextColor(5, 150, 105);  // Set green color
        doc.text(section.heading, margin, yPosition);
        yPosition += 12; // Reduced from 12

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(31, 41, 55);  // Reset to default dark gray
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          // Check for page break before each paragraph
          if (yPosition + 50 > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          
          yPosition = processList(paragraph, yPosition);
          yPosition += 4; // Reduced from 10
        });

        yPosition += 15; // Reduced from 15
      });

      // Ensure CTA doesn't overlap footer
      if (yPosition + 50 > pageHeight - footerHeight - 15) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 12;
      }
      
      yPosition += 8;
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata
    doc.setProperties({
      title: title,
      subject: 'Advanced Grant Strategy Framework',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact',
      description: 'Strategic framework for developing and managing institutional grant funding, focusing on sustainable growth, compliance excellence, and impact optimization.',
      modDate: new Date()
    });

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export const generateGrantFindingApplicationOverview = async () => {
  return generatePDF(
    grantFindingApplicationStrategies.sections,
    grantFindingApplicationStrategies.title,
    grantFindingApplicationStrategies.keywords
  );
};